import { useEffect } from 'react';
import { Key, Clock, AlertTriangle, CheckCircle, Zap, Shield } from 'lucide-react';
import useCcmLicenseStore from '../stores/ccmLicenseStore';
import useAuthStore from '../stores/authStore';
import { useBranding } from '../contexts/BrandingContext';

const SKU_LABELS = {
  TRIAL: 'Trial',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

const SKU_COLORS = {
  TRIAL: '#f59e0b',
  PRO: '#22c55e',
  ENTERPRISE: '#a855f7',
};

const UsageRow = ({ label, current, max }) => {
  const unlimited = max >= 999;
  const percent = unlimited ? 0 : Math.min(100, Math.round((current / Math.max(1, max)) * 100));
  const over = current > max;
  return (
    <div className="ccm-license-usage-row">
      <div className="ccm-license-usage-label">
        <span>{label}</span>
        <span className={over ? 'ccm-license-over' : ''}>
          {current} / {unlimited ? '\u221E' : max}
        </span>
      </div>
      {!unlimited && (
        <div className="ccm-license-usage-bar">
          <div
            className="ccm-license-usage-bar-fill"
            style={{
              width: `${percent}%`,
              background: over ? '#ef4444' : percent > 80 ? '#f59e0b' : '#22c55e',
            }}
          />
        </div>
      )}
    </div>
  );
};

const CcmLicenseCard = () => {
  const { license, loading, fetchLicense } = useCcmLicenseStore();
  const { tenantVersion } = useAuthStore();
  const { branding } = useBranding();

  useEffect(() => {
    fetchLicense();
  }, [tenantVersion, fetchLicense]);

  if (loading) {
    return (
      <div className="card">
        <h3 className="card-title"><Key size={20} /> CCM Licensing</h3>
        <p className="form-hint">Loading license information...</p>
      </div>
    );
  }

  if (!license || !license.hasLicense) {
    return (
      <div className="card">
        <h3 className="card-title"><Key size={20} /> CCM Licensing</h3>
        <div className="ccm-license-empty">
          <Shield size={32} />
          <p>{license?.message || 'No CCM license is assigned to this site.'}</p>
          <p className="form-hint">Contact your administrator to assign a license.</p>
        </div>
      </div>
    );
  }

  const skuColor = SKU_COLORS[license.sku] || '#9ca3af';
  const skuLabel = SKU_LABELS[license.sku] || license.sku;

  const handleUpgrade = () => {
    window.open('mailto:sales@serverli.com?subject=CCM%20License%20Upgrade', '_blank');
  };

  return (
    <div className="card">
      <h3 className="card-title"><Key size={20} /> CCM Licensing</h3>

      <div className="ccm-license-card">
        <div className="ccm-license-header">
          <div className="ccm-license-sku-badge" style={{ background: `${skuColor}22`, color: skuColor, borderColor: `${skuColor}55` }}>
            {skuLabel}
          </div>
          {license.isExpired ? (
            <div className="ccm-license-status-badge expired">
              <AlertTriangle size={14} /> Expired
            </div>
          ) : license.isTrial ? (
            <div className="ccm-license-status-badge trial">
              <Clock size={14} /> Trial - {license.daysRemaining} days left
            </div>
          ) : (
            <div className="ccm-license-status-badge active">
              <CheckCircle size={14} /> Active
            </div>
          )}
        </div>

        {license.isExpired && (
          <div className="ccm-license-warning">
            <AlertTriangle size={16} />
            {license.isTrial
              ? `Your free trial has expired. Upgrade to Pro to continue using ${branding.brandName}.`
              : 'Your license has expired. Your site is in read-only mode. Upgrade to Pro for full access.'}
          </div>
        )}

        <div className="ccm-license-usage">
          <UsageRow label="Endpoints" current={license.currentEndpoints || 0} max={license.maxEndpoints || 0} />
          <UsageRow label="Users" current={license.currentUsers || 0} max={license.maxUsers || 0} />
          <UsageRow label="Integrations" current={license.currentIntegrations || 0} max={license.maxIntegrations || 0} />
        </div>

        {(license.sku !== 'ENTERPRISE' || license.isExpired) && (
          <div className="ccm-license-actions">
            <button className="btn btn-primary" onClick={handleUpgrade} data-upgrade="ccm">
              <Zap size={16} /> {license.isTrial ? 'Upgrade to Pro' : 'Contact Sales'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CcmLicenseCard;
